import type { RidePickupPoint } from '@/services/platform/data/booking/rides/pickupPoints';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils/cn';

export interface LocationMiniCardProps {
  point: RidePickupPoint;
  onClick: () => void;
  /** True while resolving coordinates for an address-book entry that has none yet. */
  isLoading?: boolean;
  /** True when this point is the currently selected pickup/dropoff. */
  isSelected?: boolean;
  /** Overrides the default `bg-jncquoi-neutral-00` background, e.g. `"bg-jncquoi-neutral-50"`. */
  className?: string;
}

/** Small horizontal-scroll card: bold name + "City | Address" secondary line. */
export function LocationMiniCard({
  point,
  onClick,
  isLoading,
  isSelected,
  className,
}: LocationMiniCardProps) {
  const t = useClientTranslation();
  const secondaryLine = [point.city, point.address].filter(Boolean).join(' | ');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      aria-busy={isLoading}
      aria-pressed={isSelected}
      className={cn(
        'bg-jncquoi-neutral-00 flex h-full w-37 shrink-0 cursor-pointer flex-col gap-1 overflow-hidden rounded-[2px] border p-2 text-left',
        className,
        isSelected ? 'border-jncquoi-neutral-800' : 'border-transparent',
        isLoading && 'cursor-wait opacity-60'
      )}
    >
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 truncate text-[12px] font-medium tracking-[0.5px]">
        {point.name}
      </span>
      {(secondaryLine || isLoading) && (
        <span className="font-pp-neue-montreal text-jncquoi-neutral-800 truncate text-[12px] tracking-[0.5px]">
          {isLoading ? t('App.Labels.Locating') : secondaryLine}
        </span>
      )}
    </button>
  );
}

export interface LocationListRowProps {
  point: RidePickupPoint;
  onClick: () => void;
  /** True while resolving coordinates for an address-book entry that has none yet. */
  isLoading?: boolean;
}

/** Full-width row: bold name + "City | Address" secondary line — used for flat lists (recent trips, search results). */
export function LocationListRow({ point, onClick, isLoading }: LocationListRowProps) {
  const t = useClientTranslation();
  const secondaryLine = [point.city, point.address].filter(Boolean).join(' | ');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      aria-busy={isLoading}
      className={cn(
        'flex w-full cursor-pointer flex-col gap-1 p-2 text-left',
        isLoading && 'cursor-wait opacity-60'
      )}
    >
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] font-medium tracking-[0.5px]">
        {point.name}
      </span>
      {(secondaryLine || isLoading) && (
        <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px]">
          {isLoading ? t('App.Labels.Locating') : secondaryLine}
        </span>
      )}
    </button>
  );
}
